import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { eventsApi, StreamEventDto } from '../../services/api/events.api'
import {
  subscribeChannel,
  unsubscribeChannel,
} from '../../services/centrifugo/centrifugoClient'

interface UseNodeEventsResult {
  events: StreamEventDto[]
  isLoadingHistory: boolean
  isLive: boolean
}

const MAX_BUFFER = 2000

/**
 * Subscribes to the per-node Centrifugo channel
 * `run:{runId}:node:{nodeExecutionId}` and backfills history filtered to
 * that one node via the REST endpoint. Returns a chronological event
 * buffer (oldest first), capped at MAX_BUFFER events in memory.
 *
 * History is deduplicated against live events by `event_id` so a slow
 * reconnect followed by a backfill doesn't double-count.
 */
export function useNodeEvents(
  runId: string | null | undefined,
  nodeExecutionId: string | null | undefined,
): UseNodeEventsResult {
  const [live, setLive] = useState<StreamEventDto[]>([])
  const [isLive, setIsLive] = useState(false)
  const seenIds = useRef<Set<string>>(new Set())

  const historyQuery = useQuery({
    queryKey: ['node-events-history', runId, nodeExecutionId],
    queryFn: () =>
      eventsApi.list(runId!, {
        node_execution_id: nodeExecutionId!,
        limit: 500,
      }),
    enabled: Boolean(runId && nodeExecutionId),
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!runId || !nodeExecutionId) return
    seenIds.current = new Set()
    let active = true
    let handle: Awaited<ReturnType<typeof subscribeChannel>> | null = null

    const channel = `run:${runId}:node:${nodeExecutionId}`
    subscribeChannel(runId, nodeExecutionId, channel, (data) => {
      const ev = data as StreamEventDto
      if (seenIds.current.has(ev.event_id)) return
      seenIds.current.add(ev.event_id)
      setLive((prev) => {
        const next = [...prev, ev]
        return next.length > MAX_BUFFER ? next.slice(-MAX_BUFFER) : next
      })
    })
      .then((h) => {
        if (!active) {
          // Effect cleaned up before subscribe resolved — unsubscribe now.
          unsubscribeChannel(h)
          return
        }
        handle = h
        setIsLive(true)
      })
      .catch(() => {
        if (active) setIsLive(false)
      })

    return () => {
      active = false
      setIsLive(false)
      if (handle) unsubscribeChannel(handle)
    }
  }, [runId, nodeExecutionId])

  // Merge history + live into one chronological buffer, deduped by event_id.
  // This MUST be pure: it uses a fresh local Set each run rather than the
  // `seenIds` ref (which the subscribe effect owns for live-append dedup).
  // A prior version mutated `seenIds` here, so on recompute the history rows
  // were already "seen" and got dropped — every live event wiped the history.
  const events = useMemo<StreamEventDto[]>(() => {
    const seen = new Set<string>()
    const out: StreamEventDto[] = []
    for (const ev of historyQuery.data?.events ?? []) {
      if (!seen.has(ev.event_id)) {
        seen.add(ev.event_id)
        out.push(ev)
      }
    }
    for (const ev of live) {
      if (!seen.has(ev.event_id)) {
        seen.add(ev.event_id)
        out.push(ev)
      }
    }
    return out
  }, [historyQuery.data, live])

  return {
    events,
    isLoadingHistory: historyQuery.isLoading,
    isLive,
  }
}
